import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { localizePath, useLang } from '../i18n';

import LazerKesimVideo from '../images/laser_video.mp4';
import LazerKesimPoster from '../images/laser_video_poster.jpg';
// 3D animations of a bus seat hinge arm (animations/hinge_arm.py)
import AbkantVideo from '../images/hizmet_abkant.mp4';
import AbkantPoster from '../images/hizmet_abkant.jpg';
import KaynakVideo from '../images/hizmet_kaynak.mp4';
import KaynakPoster from '../images/hizmet_kaynak.jpg';
import TasarimVideo from '../images/hizmet_tasarim.mp4';
import TasarimPoster from '../images/hizmet_tasarim.jpg';

const ServiceSection = ({ id, title, image, video, videoLabel, reverse, children }) => (
    <section id={id} className="scroll-mt-20 py-12 border-b border-gray-200 last:border-b-0">
        <div className={`flex flex-col gap-8 items-center ${reverse ? 'md:flex-row-reverse' : 'md:flex-row'}`}>
            <div className="md:w-1/2">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-0 mb-4">{title}</h2>
                <div className="text-lg text-gray-700 leading-relaxed space-y-4">
                    {children}
                </div>
            </div>
            <div className="md:w-1/2 w-full">
                {video ? (
                    <>
                        {/* The poster image replaces the video for reduced-motion users */}
                        <video
                            className="w-full aspect-video object-cover rounded-xl shadow-md bg-gray-900 motion-reduce:hidden"
                            src={video}
                            poster={image}
                            autoPlay
                            loop
                            muted
                            playsInline
                            aria-label={videoLabel}
                        />
                        <img src={image} alt={title} loading="lazy" className="hidden motion-reduce:block w-full aspect-video object-cover rounded-xl shadow-md" />
                    </>
                ) : (
                    <img src={image} alt={title} loading="lazy" className="w-full h-72 object-cover rounded-xl shadow-md" />
                )}
            </div>
        </div>
    </section>
);

const CONTENT = {
    tr: {
        title: 'Hizmetler',
        description: 'Lazer kesim, abkant büküm, kaynaklı imalat ve 2D/3D tasarım hizmetleri. 1500x3000 mm lazer tablası, 3000 mm büküm kapasitesi.',
        heading: 'Hizmetlerimiz',
        laser: {
            title: 'Lazer Kesim',
            videoLabel: 'Lazer kesim videosu',
            intro: 'Akka Demir Çelik olarak, endüstrinin gelişimini takip ederek son teknoloji lazer kesim tezgahlarımızla hizmet vermekteyiz. Bu doğrultuda:',
            materials: [
                'HRP-DKP-ST37-52 Sac: 0.5 mm - 20 mm',
                'Paslanmaz Sac: 0.5 mm - 10 mm',
                'Alüminyum Sac: 0.5 mm - 10 mm',
                'Hardox Sac: 0.5 mm - 20 mm',
            ],
            capacity: 'Maksimum 1500x3000 mm sac işleme kapasitesine sahibiz.',
        },
        bending: {
            title: 'Abkant Büküm',
            videoLabel: 'Abkant pres büküm animasyonu',
            body: 'Hassasiyet değeri yüksek abkant pres tezgahlarımız sayesinde tolerans değerlerine uygun büküm hizmeti verebilmekteyiz.',
            capacity: 'Maksimum 3000 mm boyunda bükme kapasitesi.',
        },
        welding: {
            title: 'Kaynaklı İmalat',
            videoLabel: 'Kaynak animasyonu',
            body: 'Kaynak hattımız ve konusunda uzman personelimiz ile otomotiv, makine, raylı sistemler, savunma sanayi ve mobilya sektörlerine hizmet vermekteyiz.',
        },
        design: {
            title: 'Tasarım',
            videoLabel: 'Tasarım ve açınım animasyonu',
            body: '2D ve 3D modelleme, tasarım ve modellenmiş her türlü veriyi üretebilme tecrübe ve yeteneğimiz bulunmaktadır.',
        },
        cta: 'Projeniz için fiyat teklifi almak ister misiniz?',
        ctaButton: 'Teklif Al',
    },
    en: {
        title: 'Services',
        description: 'Laser cutting, press brake bending, welded fabrication and 2D/3D design services. 1500×3000 mm laser bed, 3000 mm bending length.',
        heading: 'Our Services',
        laser: {
            title: 'Laser Cutting',
            videoLabel: 'Laser cutting video',
            intro: 'At Akka Demir Çelik, we keep up with the industry and cut with state-of-the-art laser machines. Our capabilities:',
            materials: [
                'HRP-DKP-ST37-52 sheet: 0.5 mm - 20 mm',
                'Stainless steel sheet: 0.5 mm - 10 mm',
                'Aluminium sheet: 0.5 mm - 10 mm',
                'Hardox sheet: 0.5 mm - 20 mm',
            ],
            capacity: 'We can process sheets up to 1500x3000 mm.',
        },
        bending: {
            title: 'Press Brake Bending',
            videoLabel: 'Press brake bending animation',
            body: 'Our high-precision press brakes let us deliver bends within your tolerances.',
            capacity: 'Bending lengths up to 3000 mm.',
        },
        welding: {
            title: 'Welded Fabrication',
            videoLabel: 'Welding animation',
            body: 'With our welding line and expert staff, we serve the automotive, machinery, rail systems, defence and furniture industries.',
        },
        design: {
            title: 'Design',
            videoLabel: 'Design and flat pattern animation',
            body: 'We have the experience and capability for 2D and 3D modelling, design, and manufacturing from any modelled data.',
        },
        cta: 'Would you like a quote for your project?',
        ctaButton: 'Get a Quote',
    },    de: {
        title: 'Leistungen',
        description: 'Laserschneiden, Abkanten, Schweißbaugruppen und 2D/3D-Konstruktion. Laserschneidtisch 1500 × 3000 mm, Biegelängen bis 3000 mm.',
        heading: 'Unsere Leistungen',
        laser: {
            title: 'Laserschneiden',
            videoLabel: 'Video zum Laserschneiden',
            intro: 'Bei Akka Demir Çelik verfolgen wir die Entwicklung der Branche und schneiden auf modernsten Laserschneidanlagen. Unsere Möglichkeiten:',
            materials: [
                'Bleche HRP, DKP, St37–52: 0,5 mm – 20 mm',
                'Edelstahlblech: 0,5 mm – 10 mm',
                'Aluminiumblech: 0,5 mm – 10 mm',
                'Hardox: 0,5 mm – 20 mm',
            ],
            capacity: 'Wir bearbeiten Blechformate bis 1500 × 3000 mm.',
        },
        bending: {
            title: 'Abkanten',
            videoLabel: 'Animation: Abkanten auf der Abkantpresse',
            body: 'Mit unseren präzisen Abkantpressen biegen wir innerhalb Ihrer Toleranzen.',
            capacity: 'Biegelängen bis 3000 mm.',
        },
        welding: {
            title: 'Schweißbaugruppen',
            videoLabel: 'Animation: Schweißen',
            body: 'Mit unserer Schweißlinie und erfahrenen Fachkräften beliefern wir die Automobilindustrie, den Maschinenbau, die Bahntechnik, die Verteidigungsindustrie und die Möbelindustrie.',
        },
        design: {
            title: 'Konstruktion',
            videoLabel: 'Animation: Konstruktion und Abwicklung',
            body: 'Wir haben die Erfahrung und die Möglichkeiten für 2D- und 3D-Modellierung, Konstruktion und die Fertigung nach beliebigen Modelldaten.',
        },
        cta: 'Möchten Sie ein Angebot für Ihr Projekt?',
        ctaButton: 'Angebot anfordern',
    },
};

const Services = () => {
    const lang = useLang();
    const c = CONTENT[lang];

    return (
        <Layout title={c.title} description={c.description}>
            <div className="bg-white">
                <div className="container mx-auto px-4 py-12 max-w-5xl">
                    <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mt-0 mb-2">{c.heading}</h1>
                    <div className="w-24 h-1 bg-blue-600 rounded mb-4"></div>

                    <ServiceSection id="lazer-kesim" title={c.laser.title} image={LazerKesimPoster} video={LazerKesimVideo} videoLabel={c.laser.videoLabel}>
                        <p>{c.laser.intro}</p>
                        <ul className="list-disc pl-6 space-y-1 text-base">
                            {c.laser.materials.map((item) => <li key={item}>{item}</li>)}
                        </ul>
                        <p className="text-base text-gray-600">{c.laser.capacity}</p>
                    </ServiceSection>

                    <ServiceSection id="abkant-bukum" title={c.bending.title} image={AbkantPoster} video={AbkantVideo} videoLabel={c.bending.videoLabel} reverse>
                        <p>{c.bending.body}</p>
                        <p className="text-base text-gray-600">{c.bending.capacity}</p>
                    </ServiceSection>

                    <ServiceSection id="kaynakli-imalat" title={c.welding.title} image={KaynakPoster} video={KaynakVideo} videoLabel={c.welding.videoLabel}>
                        <p>{c.welding.body}</p>
                    </ServiceSection>

                    <ServiceSection id="tasarim" title={c.design.title} image={TasarimPoster} video={TasarimVideo} videoLabel={c.design.videoLabel} reverse>
                        <p>{c.design.body}</p>
                    </ServiceSection>

                    <div className="mt-8 rounded-xl bg-gray-900 text-white p-8 flex flex-col md:flex-row items-center justify-between gap-4">
                        <p className="text-xl font-semibold m-0">{c.cta}</p>
                        <Link to={localizePath('/contact', lang)} className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white hover:text-white font-semibold rounded-lg transition duration-300">
                            {c.ctaButton}
                        </Link>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default Services;
